import React, { useCallback, useContext, useMemo, useState } from "react";
import { VenueContext } from "contexts";
import { useApi, useMount } from "hooks";
import { ModuleWrapper } from "components/fragments";
import { HeaderA } from "components/headers";
import { Panel, Title, Text, Pill, Field, Select, RangePicker, LineChart, PieChart } from "components/commons";
import ChartContainer from "modules/dashboard/chart-container";
import lang from "translations";
import * as moment from "moment";
import { toApiDateTimeFormat, prepareChart } from "services";
import { getOrderVolumeGraph, getOrderStatusKpi } from "apis/order.api";
import { orderVolumeGraph, orderStatusKpi } from "mappers/order-status-history.mapper";

const splitTypeOptions = [
  { text: lang.hour, value: "HOUR" },
  { text: lang.day, value: "DAY" },
  { text: lang.month, value: "MONTH" },
];

// KPI dashboard for venue_order/venue_order_status_history/venue_order_item_status_history
// -- previously write-only with no read surface, and "order" had no page/module anywhere
// in this frontend at all (closest existing concepts were Transaction and Booking, neither
// of which is this). Status names are venue-specific free text, so avgDurationSecondsByStatus
// is reported per actual status name rather than a single hardcoded "fulfillment time".
const OrderKpi = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;

  const [dateRange, setDateRange] = useState([moment().subtract(7, "days").startOf("day"), moment().endOf("day")]);
  const [splitType, setSplitType] = useState("DAY");

  const { request: requestGraph, loading: loadingGraph, mappedData: graphData } = useApi({
    api: getOrderVolumeGraph,
    mapper: orderVolumeGraph,
    handleOwnError: true,
  });

  const { request: requestKpi, loading: loadingKpi, mappedData: kpiData } = useApi({
    api: getOrderStatusKpi,
    mapper: orderStatusKpi,
    handleOwnError: true,
  });

  const fetchAll = useCallback(
    (range = dateRange, split = splitType) => {
      const startDateTime = toApiDateTimeFormat(range[0]);
      const endDateTime = toApiDateTimeFormat(range[1], true);
      requestGraph({ venueId, startDateTime, endDateTime, splitType: split, interval: split });
      requestKpi({ venueId, startDateTime, endDateTime });
    },
    [dateRange, splitType, venueId, requestGraph, requestKpi]
  );

  useMount(() => {
    fetchAll();
  });

  const onDateRangeChange = useCallback(
    (name, value) => {
      setDateRange(value);
      fetchAll(value, splitType);
    },
    [fetchAll, splitType]
  );

  const onSplitTypeChange = useCallback(
    (name, { value }) => {
      setSplitType(value);
      fetchAll(dateRange, value);
    },
    [fetchAll, dateRange]
  );

  const statusDistributionChart = useMemo(() => {
    const entries = Object.entries(kpiData.currentStatusDistribution || {});
    return prepareChart(entries.map(([name, value]) => ({ name, value })));
  }, [kpiData.currentStatusDistribution]);

  return (
    <ModuleWrapper header={<HeaderA title={<Title xl>{lang.orderKpi}</Title>} description={lang.orderKpiDescription} />}>
      <Panel className="mb-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field filterLabel={lang.dateRange}>
            <RangePicker name="dateRange" value={dateRange} onChange={onDateRangeChange} />
          </Field>
          <Field filterLabel={lang.granularity}>
            <Select name="splitType" value={splitType} options={splitTypeOptions} onChange={onSplitTypeChange} />
          </Field>
        </div>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-md">
        <Panel>
          <Field label={lang.totalOrders}>
            <Text size="text-xl" strong>
              {loadingKpi ? "-" : kpiData.totalOrders ?? 0}
            </Text>
          </Field>
        </Panel>
        <Panel>
          <Field label={lang.avgItemsPerOrder}>
            <Text size="text-xl" strong>
              {loadingKpi ? "-" : (kpiData.avgItemsPerOrder ?? 0).toFixed(1)}
            </Text>
          </Field>
        </Panel>
        <Panel>
          <Field label={lang.avgDurationByState}>
            <div className="flex flex-wrap gap-2">
              {Object.entries(kpiData.avgDurationSecondsByStatus || {}).map(([status, avgSeconds]) => (
                <Pill key={status} size="text-xs">
                  {status}: {Math.round(avgSeconds / 60)}m avg
                </Pill>
              ))}
            </div>
          </Field>
        </Panel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartContainer title={lang.orderVolume} loading={loadingGraph} empty={!graphData.data?.data?.length}>
          <LineChart height={280} {...graphData.data} />
        </ChartContainer>
        <ChartContainer title={lang.currentStateDistribution} loading={loadingKpi} empty={!statusDistributionChart.data?.length}>
          <PieChart className="mt-lg" {...statusDistributionChart} legendName={lang.status} legendValue={lang.totalOrders} />
        </ChartContainer>
      </div>
    </ModuleWrapper>
  );
};

export default OrderKpi;
